import { BACKEND_URL } from "../../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Vendor Admin (thread #1340) — RTK Query API for the Vendor Admin portal page.
// The BFF enforces the gates server-side on every call via the TABC table
// (userHasTrainingButton in vendorAdminController), so a token without the
// position gets 403 and the UI shows the gate message. Reads hit
// airtable_mirror.companies_sjm8 (no live Airtable); update/delete are gated on
// VENDOR_ADMIN_WRITEBACK_ENABLED on the BFF and return 423 (write-back not
// enabled) until Levi flips the flag.
//
// TWO GATES, NOT ONE. listVendors/getVendor and update/delete require "Vendor
// Admin" (President). The three calls the Add-Vendor FORM makes — link options,
// create, create-contact — require "Add Vendor" (five positions), because a form
// five positions can open must be able to answer its own calls. This header said
// "every call" was President-gated until thread #1717; that stopped being true
// on 2026-10-08.
export const vendorAdminApi = createApi({
  reducerPath: "vendorAdminApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Vendor", "VendorAccess", "VendorGrantable"],
  // 2026-09-22, thread #1340. The Vendor Admin page pre-warms the Access
  // dialog's cache at page load (see the prefetch effect in
  // VendorAdminContent). The default 60s keepUnusedDataFor is shorter than a
  // typical session on this page, and an evicted entry would put the dialog
  // back on a click-time fetch — the exact path that stalls. Hold warm entries
  // for the life of the page instead.
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    listVendors: builder.query({
      query: () => `/vendor-admin/portal/list`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Vendor", id })),
              { type: "Vendor", id: "LIST" },
            ]
          : [{ type: "Vendor", id: "LIST" }],
    }),
    getVendor: builder.query({
      query: (recid) => `/vendor-admin/portal/get/${recid}`,
      providesTags: (result, error, recid) => [{ type: "Vendor", id: recid }],
    }),
    // The Add/Edit Vendor form's linked-record picker source (thread #1569 r10,
    // Levi 2026-10-09: the form "doesn't allow me to select a Location … same
    // with Contact linked record field, also the 'Use' linked record field").
    //
    // ITS OWN ENDPOINT, NOT THE INVENTORY ONE, and that is an access decision
    // rather than a data one: the request is gated on the "Add Vendor" TABC row,
    // which five positions hold, while "Inventory Admin" — the gate on
    // /inventory-admin/portal/link-options — is held by the President alone. The
    // read itself is the same shared resolver on the BFF (resolveLinkOptions), so
    // the two pages cannot answer the same field with two different option sets.
    //
    // `ids` is sent comma-joined because the BFF splits it that way; an empty
    // array is omitted rather than sent as "", which the server reads as "no ids
    // to resolve" and rejects when there is no q either.
    getVendorLinkOptions: builder.query({
      query: ({ field, q, ids, limit } = {}) => ({
        url: `/vendor-admin/portal/link-options`,
        params: {
          field,
          ...(q ? { q } : {}),
          ...(ids && ids.length ? { ids: ids.join(",") } : {}),
          ...(limit ? { limit } : {}),
        },
      }),
    }),
    createVendor: builder.mutation({
      query: (body) => ({
        url: `/vendor-admin/portal/create`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Vendor", id: "LIST" }],
    }),
    // "Add Contact" inside the Add Vendor form (thread #1717, Levi 2026-10-21).
    //
    // ITS OWN ENDPOINT, for the same access reason as getVendorLinkOptions
    // above: the Contacts row it creates lands in an Inventory Admin table, but
    // /inventory-admin/portal/create is gated on the President-only "Inventory
    // Admin" row, and the Add-Vendor form is reachable by five positions. This
    // endpoint gates on "Add Vendor" and the BFF pins the table to Contacts, so
    // the wider gate buys nothing beyond one Contacts row.
    //
    // Body is `{ values: { name, phone, email, position, notes } }` — the same
    // `values` envelope the inventory create takes, because the BFF routes both
    // through one write path.
    createVendorContact: builder.mutation({
      query: (values) => ({
        url: `/vendor-admin/portal/create-contact`,
        method: "POST",
        body: { values },
      }),
    }),
    updateVendor: builder.mutation({
      query: ({ recid, ...body }) => ({
        url: `/vendor-admin/portal/update/${recid}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { recid }) => [
        { type: "Vendor", id: recid },
        { type: "Vendor", id: "LIST" },
      ],
    }),
    deleteVendor: builder.mutation({
      query: (recid) => ({
        url: `/vendor-admin/portal/delete/${recid}`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Vendor", id: "LIST" }],
    }),
    // Vendor Access (thread #1340, PR 4) — per-vendor Position/Location grants.
    listVendorAccess: builder.query({
      query: (vendorRecId) => `/vendor-access/portal/list/${vendorRecId}`,
      providesTags: (result, error, vendorRecId) => [{ type: "VendorAccess", id: vendorRecId }],
    }),
    // Every vendor's grants in ONE read (thread #1576, 2026-10-09). Backs the
    // list's access column and its "who has access" filter: the per-vendor
    // query above answers "who can see this vendor" for one row, and asking it
    // per row is one request per vendor. The SUMMARY id is a single shared
    // cache entry, distinct from the per-vendor ids, so warming one does not
    // evict the other; grant/revoke/bulk invalidate both.
    accessSummary: builder.query({
      query: () => `/vendor-access/portal/summary`,
      providesTags: [{ type: "VendorAccess", id: "SUMMARY" }],
    }),
    // includeTerminated: pass `true` for the "include people who have left"
    // view. The two variants are separate cache entries, so the default active
    // roster is never displaced by the wider one (thread 1a116e66c0026535).
    listGrantableTargets: builder.query({
      query: (includeTerminated) =>
        `/vendor-access/portal/grantable${includeTerminated ? "?include_terminated=1" : ""}`,
      providesTags: [{ type: "VendorGrantable", id: "ALL" }],
    }),
    grantVendorAccess: builder.mutation({
      query: (body) => ({
        url: `/vendor-access/portal/grant`,
        method: "POST",
        body,
      }),
      // The shared SUMMARY entry feeds the list's access column AND its
      // filter options, so a grant has to refresh that entry as well as the
      // changed vendor's dialog — otherwise the column keeps the old count
      // until the page is reloaded (thread #1576).
      invalidatesTags: (result, error, { vendorRecId }) => [
        { type: "VendorAccess", id: vendorRecId },
        { type: "VendorAccess", id: "SUMMARY" },
      ],
    }),
    revokeVendorAccess: builder.mutation({
      query: (body) => ({
        url: `/vendor-access/portal/revoke`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { vendorRecId }) => [
        { type: "VendorAccess", id: vendorRecId },
        { type: "VendorAccess", id: "SUMMARY" },
      ],
    }),
    // Bulk apply (thread #1340 r17) — one subject across the vendors Levi
    // ticked in the list. Invalidates each touched vendor's grants so the
    // per-vendor Access dialog reflects the change immediately.
    bulkVendorAccess: builder.mutation({
      query: (body) => ({
        url: `/vendor-access/portal/bulk`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { vendorRecIds }) => [
        ...(vendorRecIds || []).map((id) => ({ type: "VendorAccess", id })),
        { type: "VendorAccess", id: "SUMMARY" },
      ],
    }),
  }),
});

// The vendor page's picker wiring, in the shape AdminTableContent's own
// `services` map exposes (POSITION_SERVICES / INVENTORY_SERVICES). The picker's
// `linkArgs` contract is (tableKey, {field, ids, q}) and the vendor endpoint
// keys on the FIELD alone — the BFF derives the target table from that field's
// own linkedTableId — so the tableKey is ignored here exactly as the Inventory
// page ignores the link target it already knows.
export const vendorLinkArgs = (_tableKey, { field, ids, q } = {}) => ({
  field: field.key,
  ...(ids && ids.length ? { ids } : {}),
  ...(q ? { q } : {}),
});

export const {
  useListVendorsQuery,
  useGetVendorQuery,
  useLazyGetVendorLinkOptionsQuery,
  useCreateVendorMutation,
  useCreateVendorContactMutation,
  useUpdateVendorMutation,
  useDeleteVendorMutation,
  useListVendorAccessQuery,
  useAccessSummaryQuery,
  useListGrantableTargetsQuery,
  useGrantVendorAccessMutation,
  useRevokeVendorAccessMutation,
  useBulkVendorAccessMutation,
} = vendorAdminApi;
